import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AddUserDialogComponent } from '@components/add-user-dialog/add-user-dialog.component';
import { EmpyreanApplicationName, IUserLevelClient, UserLevel, UserLevelApplicationLinks } from '@interfaces/user-level';
import { User } from '@models/user';
import { ActiveUsersToggleFilter, UserListFilter } from '@models/user-list';
import { UserStatus } from '@models/user-status';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { UserService } from '@services/user/user.service';
import { openDialogConfirmation } from '@shared/swal-dialogs';
import { BehaviorSubject, combineLatest, forkJoin, Observable, firstValueFrom } from 'rxjs';
import { defaultIfEmpty, map } from 'rxjs/operators';
import * as ExcelJS from 'exceljs/dist/exceljs.min.js'
import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs';
import { ToastService } from '@services/toast-service/toast.service';
import { Persona } from '@models/persona';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  standalone: false
})
export class UserListComponent implements OnInit {
  public fetching = false;
  public tableFilter$ = new BehaviorSubject(new UserListFilter());
  public processingUserId: number = null;
  public userList$: Observable<User[]>;
  public userLevelList: IUserLevelClient[];
  public selectedToggleFilter: ActiveUsersToggleFilter;
  private currentUserId: number;
  private currentUserLevelList: UserLevel[];
  private excelfileName = 'UsersListExport.xlsx';
  private userList = [];
  userListSubscription: Subscription | undefined;
  public isExporting = false;
  public personas: Persona[] = [];

  public get hasExplorerLicence(): boolean {
    return this.userLevelList?.some(level =>
      UserLevelApplicationLinks[EmpyreanApplicationName.Explorer].includes(level.level_id)
    ) ?? false;
  }

  public userHasExplorerRole(user: User): boolean {
    return user.user_levels?.some(levelId =>
      UserLevelApplicationLinks[EmpyreanApplicationName.Explorer].includes(levelId)
    ) ?? false;
  }

  public isCurrentUser(user: User) {
    return this.currentUserId === user.user_id;
  }

  public isActiveUser(user: User) {
    return user.record_status.toLowerCase() === UserStatus.A.toLowerCase();
  }

  // Expose enum to template
  public UserStatus = UserStatus;

  constructor(
    private readonly userService: UserService,
    private readonly auth: OpenIdConnectService,
    public dialog: MatDialog,
    private toastService: ToastService
  ) { }

  public async ngOnInit() {
    this.selectedToggleFilter = ActiveUsersToggleFilter.A;
    const userList$ = this.userService.userList$.pipe(defaultIfEmpty([] as User[]));
    this.userList$ = combineLatest(userList$, this.tableFilter$).pipe(
      map(([users, userListFilters]) => {
        this.currentUserId = this.auth.userId;
        this.currentUserLevelList = this.auth.userLevelList.map(level => level.level_id);

        users.forEach(user => {
          user.user_levels = user.user_levels.filter(ul => this.userService.userLevels[ul]);
        });

        return this.getFilteredUsers(users, userListFilters);
      })
    );

    this.fetching = true;

    try {
      const [userLevels, users, personas] = await firstValueFrom(
        forkJoin([
          this.userService.getUserLevels(),
          this.userService.getAllUsers(),
          this.userService.getPersonas()
        ])
      );
      this.userLevelList = userLevels;
      this.personas = personas;
    } catch (err) {
      this.toastService.showToast(`Error loading User data`, 'Error', 'error', null);
      console.warn(err);
    } finally {
      this.fetching = false;
    }
  }

  public getUserLevelName(level_id: number) {
    return this.userService.getUserLevelNames([level_id]);
  }

  public getPersonaName(personaId: number): string {
    const persona = this.personas.find(p => p.persona_id === personaId);
    return persona ? persona.persona_name : '';
  }

  public getPersonaNames(personaIds: number[]): string {
    if (!personaIds || personaIds.length === 0) {
      return '';
    }

    return personaIds
      .map(id => this.getPersonaName(id))
      .filter(name => name !== '')
      .join(', ');
  }

  public hasPersonas(user: User): boolean {
    return Array.isArray(user.user_personas) && user.user_personas.length > 0;
  }

  /** Single gate for showing a user's personas (Explorer role + has persona data). */
  public shouldDisplayPersonas(user: User): boolean {
    return this.userHasExplorerRole(user) && this.hasPersonas(user);
  }

  public getDisplayPersonaNames(user: User): string {
    if (!this.shouldDisplayPersonas(user)) {
      return '';
    }
    return this.getPersonaNames(user.user_personas);
  }

  public getIconClassForLevel(level_id: number): string {
    switch (true) {
      case (level_id === UserLevel.SystemAdministrator || level_id === UserLevel.HRSystemAdministrator):
        return 'icon_user';

      case (level_id === UserLevel.BiReportCreator || level_id === UserLevel.BiReportViewer):
        return 'icon-reporting';

      case (level_id > UserLevel.SystemAdministrator && level_id < 2000):
        return 'icon_liquidity_modeling';

      case (level_id >= UserLevel.DepositsAdministrator && level_id < 3000):
        return 'icon_summary';

      case (level_id >= UserLevel.DataverseAdministrator && level_id < 4000):
        return 'icon_tree';

      case (level_id >= UserLevel.ProfitabilityAdministrator && level_id <= UserLevel.OrgProfitabilityRuleCreator):
        return 'fa fa-magnifying-glass-chart';

      case (
        (level_id >= UserLevel.PlanningAdministrator && level_id <= UserLevel.PlanningViewer) ||
        (level_id >= UserLevel.PayrollAdministrator && level_id <= UserLevel.PayrollReader)
      ):
        return 'fa-calculator fas';

      case (level_id >= UserLevel.ModelIQAdmin && level_id <= UserLevel.ModelIQReadOnly):
        return 'fa-solid fa-chart-line';

      case (level_id >= UserLevel.ExplorerAdministrator && level_id <= UserLevel.ExplorerReader):
        return 'fa-regular fa-chart-bar';

      default:
        return '';
    }
  }

  private getUsersByToggleFilter(users: User[], toggleFilter: ActiveUsersToggleFilter, recordStatusFilter: UserStatus) {
    switch (toggleFilter) {
      case ActiveUsersToggleFilter.All:
        return users;
      case ActiveUsersToggleFilter.Disabled:
        return users.filter(user => user.record_status.toLowerCase() !== UserStatus.D.toLowerCase() && !user.enabled);
      case ActiveUsersToggleFilter.D:
        return users.filter(user => user.record_status.toLowerCase() === recordStatusFilter.toLowerCase());
      default:
        return users.filter(user => user.record_status.toLowerCase() === recordStatusFilter.toLowerCase() && user.enabled);
    }
  }

  private getFilteredUsers(users: User[], userListFilters: UserListFilter) {
    let filteredUsers = this.getUsersByToggleFilter(users, this.selectedToggleFilter, userListFilters.record_status);

    if (userListFilters.all === '') {
      for (const propertyName of Object.keys(userListFilters)) {
        if (userListFilters[propertyName] !== '') {
          if (propertyName === 'user_level') {
            filteredUsers = filteredUsers.filter(user =>
              this.userService.getUserLevelNames(user.user_levels).toLowerCase().includes(userListFilters.user_level.toLowerCase())
            );
          } else if (propertyName === 'persona') {
            filteredUsers = filteredUsers.filter(user =>
              this.getDisplayPersonaNames(user).toLowerCase().includes(userListFilters.persona.toLowerCase())
            );
          } else if (!['all', 'record_status'].includes(propertyName)) {
            filteredUsers = filteredUsers.filter(user => user[propertyName].toLowerCase().includes(userListFilters[propertyName]));
          }
        }
      }
    } else {
      // Filter ALL
      filteredUsers = filteredUsers.filter(
        user =>
          user.username.toLowerCase().includes(userListFilters.all) ||
          user.first_name.toLowerCase().includes(userListFilters.all) ||
          user.last_name.toLowerCase().includes(userListFilters.all) ||
          user.email.toLowerCase().includes(userListFilters.all) ||
          this.userService.getUserLevelNames(user.user_levels).toLowerCase().includes(userListFilters.all.toLowerCase()) ||
          (this.hasExplorerLicence &&
            this.getDisplayPersonaNames(user).toLowerCase().includes(userListFilters.all.toLowerCase()))
      );
    }
    return filteredUsers;
  }

  public editUserDialog(user: User) {
    this.dialog.open(AddUserDialogComponent, {
      data: { user, levels: this.userLevelList, currentUserLevelList: this.currentUserLevelList, isEditing: true },
      disableClose: true
    });
  }

  public addUserDialog() {
    this.dialog.open(AddUserDialogComponent, {
      data: { levels: this.userLevelList, currentUserLevelList: this.currentUserLevelList },
      disableClose: true
    });
  }

  public exportToExcel() {

    this.isExporting = true;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('User List');

    const columns = [
      { header: 'Username', key: 'Username' },
      { header: 'First Name', key: 'First Name' },
      { header: 'Last Name', key: 'Last Name' },
      { header: 'Email', key: 'Email' },
      { header: 'User Levels', key: 'User Levels' },
      ...(this.hasExplorerLicence ? [{ header: 'Personas', key: 'Personas' }] : []),
      { header: 'Is Enabled', key: 'Is Enabled' },
      { header: 'Status', key: 'Status' },
    ];

    worksheet.columns = columns;

    this.userListSubscription = this.userList$.subscribe(users => {
      this.userList = users.map(user => {
        const row: Record<string, string> = {
          "Username": user.username,
          "First Name": user.first_name,
          "Last Name": user.last_name,
          "Email": user.email,
          "User Levels": this.userService.getUserLevelNames(user.user_levels),
          "Is Enabled": user.enabled.toString(),
          "Status": user.record_status
        };
        if (this.hasExplorerLicence) {
          row["Personas"] = this.getDisplayPersonaNames(user);
        }
        return row;
      });
    });

    worksheet.addRows(this.userList);

    // Set the font for all cells in the worksheet to "Segoe UI"
    worksheet.eachRow(row => {
      row.eachCell({ includeEmpty: true }, cell => {
        cell.font = { name: 'Segoe UI' };
      });
    });

    // Make the first row bold, apply a background color, and change the font color
    const firstRow = worksheet.getRow(1);
    firstRow.eachCell({ includeEmpty: true }, cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFF' }, name: 'Segoe UI', size: 12 };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '37475d' } // Background color
      };
    });

    // Autofit column widths
    worksheet.columns.forEach(column => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, cell => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength + 2; // Add some padding
    });

    // Create a buffer for the Excel file
    workbook.xlsx.writeBuffer().then(data => {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, this.excelfileName); // You can use FileSaver or another method to save the file
    });

    if (this.userListSubscription) {
      this.userListSubscription.unsubscribe();
    }

    setTimeout(() => {
      this.isExporting = false;
    }, 3000);
  }

  public onChangeFilter(event: Event, field: string = '') {
    const value = (event.target as HTMLInputElement).value || '';
    const currentFilter = this.tableFilter$.getValue();
    currentFilter[field] = value.toLowerCase();
    this.tableFilter$.next(currentFilter);
  }

  private async deleteUser(user: User) {
    this.processingUserId = user.user_id;

    try {
      const deletedUser = await firstValueFrom(this.userService.deleteUser(user));
      user.record_status = UserStatus.D;
      this.onToggleChange(this.selectedToggleFilter, 'record_status');
      this.showDeleteToast(user);
      this.showSyncWarningToast(deletedUser?.sync_warnings);
    } catch (err) {
      this.toastService.showToast(`Error deleting user`, "Error", 'error', null);
      console.warn(err);
    } finally {
      this.processingUserId = null;
    }
  }

  public onDeleteUserClicked(user: User) {
    if (!this.isCurrentUser(user)) {
      const title = 'Confirm Deletion';
      const text = `Deleting the user ${user.username} will result in the removal of all the changes by the user that have not been saved yet. Do you wish to Proceed?`;
      openDialogConfirmation(title, text).then(result => {
        if (result.value) {
          this.deleteUser(user);
        }
      });
    }
  }

  private async undeleteUser(user: User) {
    this.processingUserId = user.user_id;

    try {
      await firstValueFrom(this.userService.undeleteUser(user));
      user.record_status = UserStatus.A;
      this.onToggleChange(this.selectedToggleFilter, 'record_status');
      this.toastService.showToast(`${user.username} has been undeleted`, 'Success', 'success', null);
    } catch (err) {
      this.toastService.showToast(`Error undeleting user`, 'Error', 'error', null);
      console.warn(err);
    } finally {
      this.processingUserId = null;
    }
  }

  public onUndeleteUserClicked(user: User) {
    this.undeleteUser(user);
  }

  public onToggleChange(value: ActiveUsersToggleFilter, field: string) {
    this.selectedToggleFilter = value;
    const currentFilter = this.tableFilter$.getValue();
    currentFilter[field] = value;
    this.tableFilter$.next(currentFilter);
  }

  public showSyncStatus = (level_id: number, user: User): boolean =>
    (level_id === 8001 || level_id === 8002) && user.is_bi_user_sync !== null;

  private showDeleteToast(user: User) {
    const body = `
      <div style="width: 100%;">
        <span style="font-size: 1rem; font-weight: bold;">${user.username}</span>
        has been deleted
      </div>
    `;

    // Show the toast using ngx-toastr
    this.toastService.showToast(body, "Success", "success", { timeOut: 5000, closeButton: true, tapToDismiss: false, disableTimeOut: false, enableHtml: true });
  }

  private showSyncWarningToast(warnings: string[] = []) {
    const filteredWarnings = (warnings ?? []).filter(warning => warning && warning.trim().length > 0);
    if (!filteredWarnings.length) {
      return;
    }

    this.toastService.showToast(filteredWarnings.join(' '), 'Warning', 'warning', null);
  }

  public isLevelVisible(userLevel: UserLevel): boolean {
    const isHRAdministrator = this.currentUserLevelList.includes(UserLevel.HRSystemAdministrator);
    const isSystemAdministrator = this.currentUserLevelList.includes(UserLevel.SystemAdministrator);
    const isPayrollLevel = UserLevelApplicationLinks[EmpyreanApplicationName.Payroll].includes(userLevel);

    // HR Admins cannot see or change user levels outside Payroll, Only HR Admins can see or change Payroll Levels
    if (
      (userLevel === UserLevel.HRSystemAdministrator && !isHRAdministrator) ||
      (isPayrollLevel && !isHRAdministrator) ||
      (!isPayrollLevel && userLevel !== UserLevel.HRSystemAdministrator && !isSystemAdministrator)
    ) {
      return true;
    }

    return false;
  }
}
